import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { FormikProps } from 'formik';
import {
  FDatePicker,
  FDatePickerQuickActions,
  FSwitch,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import type { ReceiptBatchFormValues } from './interfaces/ReceiptBatchFormValues';
import { NursingHomeDropdown } from '../general/NursingHomeDropdown';
import { ChangeNursingHomeModal } from '../modals/ChangeNursingHomeModal';
import { useReceiptBatchStore } from './ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';

export const renderReceiptBatchCommonFormEdit = (
  formik: FormikProps<ReceiptBatchFormValues>
): React.ReactElement => {
  return (
    <ReceiptBatchCommonForm
      formik={formik}
      disableNursingHomeSelection={true}
    />
  );
};

export const renderReceiptBatchCommonFormNew = (
  formik: FormikProps<ReceiptBatchFormValues>
): React.ReactElement => {
  return (
    <ReceiptBatchCommonForm
      formik={formik}
      disableNursingHomeSelection={false}
    />
  );
};

interface ReceiptBatchCommonFormProps<T extends ReceiptBatchFormValues> {
  formik: FormikProps<T>;
  disableNursingHomeSelection: boolean;
}

function ReceiptBatchCommonForm<T extends ReceiptBatchFormValues>({
  formik,
  disableNursingHomeSelection,
}: ReceiptBatchCommonFormProps<T>): React.ReactElement {
  const { t } = useTranslation();

  const [nursingHomes, currentNursingHomeId] = useReceiptBatchStore(
    useShallow((state) => [
      state.nursingHomes,
      state.formik?.values.nursingHomeId,
    ])
  );

  const {
    setFieldValue,
    setValues,
    setFieldError,
    initialValues: { receipts: initialReceipts },
    values: { nursingHomeId },
  } = formik;

  const [modalState, setModalState] = React.useState<{
    open: boolean;
    handleConfirm?: () => void;
    handleCancel?: () => void;
  }>({
    open: false,
    handleConfirm: undefined,
    handleCancel: undefined,
  });

  const transformedNursingHomesList = useMemo(() => {
    return (nursingHomes ?? []).map((nursingHome) => ({
      value: nursingHome.nursingHomeId,
      label: nursingHome.nursingHomeName,
    }));
  }, [nursingHomes]);

  const handleSetNursingHome = useCallback(
    (nursingHomeId: string) => {
      const newSelectedNursingHome = nursingHomes?.find(
        (nursingHome) => nursingHome.nursingHomeId === nursingHomeId
      );

      if (currentNursingHomeId === nursingHomeId) {
        // no change
        return;
      }

      const setValues = () => {
        setFieldValue('nursingHomeId', nursingHomeId);
        setFieldValue('tenant', newSelectedNursingHome?.tenant);
        setFieldValue('receipts', initialReceipts);
        setFieldError('nursingHomeId', undefined);
      };

      if (
        currentNursingHomeId !== undefined &&
        currentNursingHomeId !== nursingHomeId &&
        formik.values.receipts !== initialReceipts
      ) {
        setModalState({
          open: true,
          handleConfirm: () => {
            setValues();

            setModalState((modalState) => ({
              ...modalState,
              open: false,
            }));
          },
          handleCancel: () => {
            setFieldValue('nursingHomeId', currentNursingHomeId);

            setModalState((modalState) => ({
              ...modalState,
              open: false,
            }));
          },
        });
      } else {
        setValues();
      }
    },
    [
      nursingHomes,
      currentNursingHomeId,
      formik.values.receipts,
      initialReceipts,
      setFieldValue,
      setFieldError,
    ]
  );

  // sync values from context to formik initially
  useEffect(() => {
    if (!nursingHomeId && nursingHomes?.length === 1) {
      setTimeout(() => {
        setFieldValue('nursingHomeId', nursingHomes[0].nursingHomeId);
        setFieldValue('tenant', nursingHomes[0].tenant);
      }, 0);
    }
  }, [
    handleSetNursingHome,
    nursingHomeId,
    nursingHomes,
    setFieldValue,
    formik,
    setValues,
  ]);

  const handleIndividualDatesModeChange = useCallback(
    (__: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setValues((prevState) => ({
        ...prevState,
        individualDatesMode: checked,
        receiptDate: undefined,
      }));
    },
    [setValues]
  );

  useEffect(() => {
    // This is done as useEffect instead of in handleIndividualDatesModeChange, because of formik validation that is done based on individualDatesMode that can still be false in that callback
    if (formik.values.individualDatesMode) {
      setFieldValue('receiptDate', undefined);
      setFieldError('receiptDate', undefined);
    }
  }, [
    formik.values.individualDatesMode,
    setFieldError,
    setFieldValue,
    setValues,
  ]);

  return (
    <Grid item xs={12} lg={5.4}>
      <ChangeNursingHomeModal
        open={modalState.open}
        handleClose={modalState.handleCancel}
        handleConfirm={modalState.handleConfirm}
      />
      <Card
        sx={{
          p: 2,
          height: '100%',
        }}
      >
        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mb: 4,
            lineHeight: 0.9,
          }}
        >
          {t('receipts.batch.batch-receipt')}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={12} md={6.5}>
            <NursingHomeDropdown
              nursingHomes={transformedNursingHomesList}
              nursingHomeId={currentNursingHomeId}
              onChangeNursingHome={handleSetNursingHome}
              disabled={disableNursingHomeSelection}
              name={'nursingHomeId'}
            />
          </Grid>
          <Grid item xs={12} md={5.5}>
            <Stack
              direction="column"
              sx={{
                flex: 1,
                gap: 1,
              }}
            >
              <FDatePicker
                name="receiptDate"
                label={t('common.date')}
                formik={formik}
                maxDate={new Date()}
                disabled={formik.values.individualDatesMode}
              />
              {!(
                formik.errors['receiptDate'] && formik.touched['receiptDate']
              ) && (
                <FDatePickerQuickActions
                  name="receiptDate"
                  formik={formik}
                  disabled={formik.values.individualDatesMode}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Stack alignItems="flex-start">
            <FormGroup>
              <FormControlLabel
                control={<FSwitch formik={formik} name="generateReceipts" />}
                label={t('service-provider-app.generate-receipt')}
                labelPlacement="start"
              />
            </FormGroup>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={formik.values.individualDatesMode}
                    onChange={handleIndividualDatesModeChange}
                  />
                }
                label={t('receipts.batch.use-individual-dates')}
                labelPlacement="start"
              />
            </FormGroup>
          </Stack>
        </Grid>
      </Card>
    </Grid>
  );
}
